import { useEffect, useState } from 'react'
import firebase from '../firebase/compat'

// De churn voor het beheerdersdashboard, via de callable getPlatformChurn.
// Geen Firestore-listener: SubscriptionNotes en SubscriptionStatusEvents zijn
// server-only (geen regel = default-deny), en de notities zijn interne
// aantekeningen over klanten. Zelfde vorm als usePlatformRevenue.
//
// `error` blijft staan als de aanroep faalt: stil terugvallen op null zou er
// op de tegel uitzien als 'niemand gestopt'.
//
// AI-CONTEXT: See docs/features/platform-revenue-churn-trend.md

/**
 * @typedef {object} ChurnedCustomer
 * @property {string} compagnyId
 * @property {string} name
 * @property {Array<string>} sectors
 * @property {string|null} stoppedAt - ISO-tijdstip
 * @property {boolean} estimated - datum van het notitiedocument, niet uit het logboek
 * @property {string} note
 */

/**
 * @typedef {object} PlatformChurn
 * @property {Array<ChurnedCustomer>} customers
 * @property {Array<{period: string, total: number, bySector: Object<string, number>}>} months
 * @property {number} thisMonth
 * @property {number} total
 * @property {number} undated
 */

/**
 * @returns {{churn: PlatformChurn|null, loading: boolean, error: string|null}}
 */
const usePlatformChurn = () => {
    const [churn, setChurn] = useState(/** @type {PlatformChurn | null} */ (null))
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(/** @type {string | null} */ (null))

    useEffect(() => {
        let cancelled = false

        const fetch = async () => {
            try {
                // v2-functie in europe-west1 — region expliciet, anders belt de
                // SDK us-central1 en bestaat de functie daar niet.
                const getPlatformChurn = firebase.app().functions('europe-west1').httpsCallable('getPlatformChurn')
                const result = await getPlatformChurn({})
                if (cancelled) return
                setChurn(result.data || null)
            } catch (err) {
                console.error('usePlatformChurn error:', err)
                if (cancelled) return
                setError(err.message || 'Kon de churn niet ophalen.')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetch()
        return () => { cancelled = true }
    }, [])

    return { churn, loading, error }
}

export default usePlatformChurn
